import type { AssignBenefitsGetAssignListType } from '@/enums/assign-benefits-get-assign-list-type'
import {
  assignBenefitsSchema,
  type AssignBenefits
} from '@/models/api/assign-benefits'
import {
  assignBenefitsAssignBenefitsRequestSchema,
  type AssignBenefitsAssignBenefitsRequest
} from '@/models/api/assign-benefits-assign-benefits-request'

import type { AssignBenefitsAssignBusinessCardRequest } from '@/models/api/assign-benefits-assign-business-card-request'
import type { AssignBenefitsGetAssignList } from '@/models/api/assign-benefits-get-assign-list'
import { assignBenefitsGetAssignListSchema } from '@/models/api/assign-benefits-get-assign-list'
import type { AssignBenefitsGetDetailRequest } from '@/models/api/assign-benefits-get-detail-request'
import {
  assignBenefitsGetListSchema,
  type AssignBenefitsGetList
} from '@/models/api/assign-benefits-get-list'
import type { AssignBenefitsGetListRequest } from '@/models/api/assign-benefits-get-list-request'
import type { AssignBenefitsGetReturn } from '@/models/api/assign-benefits-get-return'
import { assignBenefitsGetReturnSchema } from '@/models/api/assign-benefits-get-return'
import type { AssignBenefitsGetReturnDetailRequest } from '@/models/api/assign-benefits-get-return-detail-request'
import type { AssignBenefitsReturnOrRestoreBenefitsRequest } from '@/models/api/assign-benefits-return-or-restore-benefits-request'
import {
  apiValidator,
  type ApiFactory,
  type ApiRequestConfig,
  type ApiResponse
} from '~library/services'

export default class AssignBenefitsApi {
  constructor(private api: ApiFactory) {}

  /**
   * API 取得指派禮遇清單
   * https://hackmd.io/bB6QJSSpQl26t2dHXHftwg
   */
  @apiValidator(assignBenefitsGetListSchema.array())
  getAssignBenefitsList(
    query?: AssignBenefitsGetListRequest,
    config?: ApiRequestConfig
  ): Promise<ApiResponse<AssignBenefitsGetList[]>> {
    return this.api.post('AssignBenefits/GetList', query, config)
  }

  /**
   * API 指派禮遇
   * https://hackmd.io/WIB-bzSJQjGeoP-ctfSlyw
   */
  @apiValidator(assignBenefitsAssignBenefitsRequestSchema.nullable())
  assignBenefits(
    data?: AssignBenefitsAssignBenefitsRequest,
    config?: ApiRequestConfig
  ): Promise<ApiResponse> {
    return this.api.post('AssignBenefits/AssignBenefits', data, config)
  }

  /**
   * API 指派禮遇(商務卡)
   * https://hackmd.io/rQqmyxaARWuOrELjMvuCJw
   */
  assignBusinessCard(
    data?: AssignBenefitsAssignBusinessCardRequest,
    config?: ApiRequestConfig
  ): Promise<ApiResponse> {
    return this.api.post('AssignBenefits/AssignBusinessCard', data, config)
  }

  /**
   * API 取得收回/歸還禮遇明細
   * https://hackmd.io/l7SE1QT8QGCvnYu3usuraw
   */
  @apiValidator(assignBenefitsGetReturnSchema)
  getReturnDetail(
    query?: AssignBenefitsGetReturnDetailRequest,
    config?: ApiRequestConfig
  ): Promise<ApiResponse<AssignBenefitsGetReturn>> {
    return this.api.post('AssignBenefits/GetReturnDetail', query, config)
  }

  /**
   * API 取得指派禮遇明細
   * https://hackmd.io/wqmWSNwzTBKEPx62GxSnhA
   */
  @apiValidator(assignBenefitsSchema.array())
  getAssignBenefits(
    query?: AssignBenefitsGetDetailRequest,
    config?: ApiRequestConfig
  ): Promise<ApiResponse<AssignBenefits[]>> {
    return this.api.post(`AssignBenefits/GetDetail`, query, config)
  }

  /**
   * API 收回/歸還禮遇
   * https://hackmd.io/YjvnUzaIRkKVmkVwLTUsmA
   */
  returnOrRestoreBenefits(
    data?: AssignBenefitsReturnOrRestoreBenefitsRequest,
    config?: ApiRequestConfig
  ): Promise<ApiResponse> {
    return this.api.post('AssignBenefits/ReturnOrRestoreBenefits', data, config)
  }

  /**
   * API 取得指派者下拉選項
   * https://hackmd.io/RHK1GTuYSmiQOW7GyEhCsQ
   */
  @apiValidator(assignBenefitsGetAssignListSchema.array())
  getAssignList(
    assigneeType: AssignBenefitsGetAssignListType,
    config?: ApiRequestConfig
  ): Promise<ApiResponse<AssignBenefitsGetAssignList[]>> {
    return this.api.get(`AssignBenefits/GetAssignList/${assigneeType}`, config)
  }
}
